import { type } from 'arktype';

// ---------------------------------------------------------------------------
// Retry policy (JSONB stored in webhook_endpoints.retry_policy)
// ---------------------------------------------------------------------------

export const retryPolicySchema = type({
  'maxAttempts?': type('number > 0').configure({
    description: 'Maximum number of delivery attempts before the delivery is considered failed.',
    examples: [5],
  }),
  'backoffType?': type("'exponential' | 'fixed'").configure({
    description: "Backoff strategy: 'exponential' multiplies the delay on each retry, 'fixed' keeps it constant.",
    examples: ['exponential'],
  }),
  'initialDelayMs?': type('number > 0').configure({
    description: 'Delay in milliseconds before the first retry attempt.',
    examples: [1000],
  }),
  'maxDelayMs?': type('number > 0').configure({
    description: 'Upper bound for any single retry delay, in milliseconds.',
    examples: [300000],
  }),
  'backoffMultiplier?': type('number > 0').configure({
    description: 'Multiplier applied to the previous delay when using exponential backoff.',
    examples: [2],
  }),
}).configure({ description: 'Retry policy for webhook delivery. All fields are optional and fall back to tenant defaults.' });

export type RetryPolicy = typeof retryPolicySchema.t;

export const DEFAULT_RETRY_POLICY: Required<RetryPolicy> = {
  maxAttempts: 5,
  backoffType: 'exponential',
  initialDelayMs: 1000,
  maxDelayMs: 300000,
  backoffMultiplier: 2,
};

// ---------------------------------------------------------------------------
// Create webhook endpoint
// ---------------------------------------------------------------------------

export const createWebhookEndpointSchema = type({
  name: type('string >= 1').configure({
    description: 'Human-readable name for this endpoint shown in admin UI.',
    examples: ['Payroll export'],
  }),
  url: type('string >= 1').configure({
    description: 'HTTPS URL that will receive webhook POST requests.',
    examples: ['https://api.example.com/smartcapacity/webhooks'],
  }),
  'secret?': type('string >= 1').configure({
    description: 'Optional HMAC signing secret. When provided, deliveries include an `X-Signature` header.',
    examples: ['whsec_xxxxxxxxxxxxxxxx'],
  }),
  event_types: type('string[] >= 1').configure({
    description: "List of event types this endpoint subscribes to. Use ['*'] to match all events.",
    examples: [['assignment.published', 'assignment.updated']],
  }),
  'is_active?': type('boolean').configure({
    description: 'Whether deliveries are attempted. Defaults to `true` when omitted.',
  }),
  'custom_headers?': type('Record<string, string> | null').configure({
    description: 'Extra HTTP headers sent on every request. `null` clears previously stored headers.',
  }),
  'retry_policy?': retryPolicySchema.or('null').configure({
    description: 'Per-endpoint retry policy overrides. `null` falls back to the tenant defaults.',
  }),
}).configure({ description: 'Payload for registering a new webhook endpoint.' });

export type CreateWebhookEndpoint = typeof createWebhookEndpointSchema.t;

// ---------------------------------------------------------------------------
// Update webhook endpoint
// ---------------------------------------------------------------------------

export const updateWebhookEndpointSchema = type({
  'name?': type('string >= 1').configure({ description: 'Updated display name.', examples: ['Payroll export'] }),
  'url?': type('string >= 1').configure({
    description: 'Updated HTTPS URL.',
    examples: ['https://api.example.com/smartcapacity/webhooks'],
  }),
  'event_types?': type('string[] >= 1').configure({
    description: "Updated list of subscribed event types. Use ['*'] to match all events.",
    examples: [['assignment.published']],
  }),
  'is_active?': type('boolean').configure({
    description: 'Activate (`true`) or pause (`false`) deliveries to this endpoint.',
  }),
  'custom_headers?': type('Record<string, string> | null').configure({
    description: 'Updated custom headers. `null` clears all custom headers.',
  }),
  'retry_policy?': retryPolicySchema.or('null').configure({
    description: 'Updated retry policy. `null` reverts to tenant defaults.',
  }),
}).configure({ description: 'Partial update for a webhook endpoint. Fields omitted remain unchanged.' });

export type UpdateWebhookEndpoint = typeof updateWebhookEndpointSchema.t;

// ---------------------------------------------------------------------------
// Webhook payload envelope (sent to external endpoints)
// ---------------------------------------------------------------------------

export interface WebhookPayloadEnvelope {
  id: string;
  event: string;
  timestamp: string;
  version: '1.0';
  data: {
    notificationId: string;
    title: string;
    message: string;
    category: string | null;
    priority: string;
    metadata: Record<string, unknown> | null;
    payload: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// Webhook endpoint response (secret masked)
// ---------------------------------------------------------------------------

export interface WebhookEndpointResponse {
  id: string;
  name: string;
  url: string;
  secret: string;
  eventTypes: string[];
  isActive: boolean;
  consecutiveFailures: number;
  customHeaders: Record<string, string> | null;
  retryPolicy: RetryPolicy;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Webhook delivery log response
// ---------------------------------------------------------------------------

export interface WebhookDeliveryLogResponse {
  id: string;
  deliveryId: string;
  endpointId: string;
  requestUrl: string;
  requestHeaders: Record<string, string> | null;
  requestBody: Record<string, unknown> | null;
  responseStatus: number | null;
  responseHeaders: Record<string, string> | null;
  responseBody: string | null;
  durationMs: number | null;
  attemptedAt: string;
}

// ---------------------------------------------------------------------------
// Circuit breaker constants
// ---------------------------------------------------------------------------

/** Default number of consecutive failures before auto-disabling an endpoint. */
export const DEFAULT_CIRCUIT_BREAKER_THRESHOLD = 10;

/** Maximum response body size (bytes) to store in delivery logs. */
export const MAX_RESPONSE_BODY_SIZE = 64 * 1024; // 64KB

/** Webhook HTTP request timeout in milliseconds. */
export const WEBHOOK_REQUEST_TIMEOUT_MS = 30_000;

/** User-Agent header sent with webhook requests. */
export const WEBHOOK_USER_AGENT = 'SmartCapacity-Webhook/1.0';

/** Webhook payload version. */
export const WEBHOOK_PAYLOAD_VERSION = '1.0' as const;
